/**
 * Guest auth session helpers.
 *
 * Guest JWTs are stored in sessionStorage (cleared on tab close).
 * Never use localStorage for a guest token.
 *
 * Chat guests and report guests are separate sessions with separate keys. A
 * report guest's token is signed with a different key from a chat guest's and
 * is useless anywhere a chat is served, so the two are never read from - or
 * cleared through - the same slot: signing in to a report cannot sign a tab
 * out of a chat, and a chat token can never be mistaken for a report one.
 */

const readItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeItem = (key, value) => {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    }
  } catch (e) {
    /* ignore */
  }
};

const removeItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
};

export const decodeGuestJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalised = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalised + "=".repeat((4 - (normalised.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

/**
 * One kind of guest's session: its JWT and the invite token that started it.
 *
 * The invite token is kept for the life of the tab. The guest JWT lasts an
 * hour and the gate signs a guest out after fifteen idle minutes, but by then
 * they are on a page whose URL no longer carries the token from their email.
 * Without it the login page has nothing to send a new code against, and a
 * guest whose session merely lapsed is told their invitation is invalid.
 * Holding it in sessionStorage - the same scope and lifetime as the JWT, gone
 * when the tab closes - lets them sign in again without hunting for the
 * original email, and keeps it out of the address bar of every page they
 * visit.
 */
const createGuestSession = ({ jwtKey, inviteTokenKey, kind, resourceType }) => {
  const getJwt = () => readItem(jwtKey);
  const getClaims = () => decodeGuestJwt(getJwt());
  return {
    jwtKey,
    getJwt,
    setJwt: (token) => writeItem(jwtKey, token),
    clearJwt: () => removeItem(jwtKey),
    getInviteToken: () => readItem(inviteTokenKey),
    setInviteToken: (token) => writeItem(inviteTokenKey, token),
    clearInviteToken: () => removeItem(inviteTokenKey),
    getClaims,
    // A token of the other kind in this slot is not a session here. Chat
    // tokens from before report sharing carry no resourceType at all.
    isValid: () => {
      const claims = getClaims();
      if (!claims || !claims.exp || claims.kind !== kind) return false;
      if ((claims.resourceType || "chat") !== resourceType) return false;
      return Date.now() < claims.exp * 1000;
    },
    getExpiryMs: () => {
      const claims = getClaims();
      if (!claims?.exp) return 0;
      return claims.exp * 1000 - Date.now();
    },
  };
};

export const chatGuestSession = createGuestSession({
  jwtKey: "guestJwt",
  inviteTokenKey: "guestInviteToken",
  kind: "guest",
  resourceType: "chat",
});

export const reportGuestSession = createGuestSession({
  jwtKey: "guestReportJwt",
  inviteTokenKey: "guestReportInviteToken",
  kind: "reportGuest",
  resourceType: "queryReport",
});

// The chat guest's session, under the names the chat has always used.
export const getGuestJwt = chatGuestSession.getJwt;
export const setGuestJwt = chatGuestSession.setJwt;
export const clearGuestJwt = chatGuestSession.clearJwt;
export const getGuestInviteToken = chatGuestSession.getInviteToken;
export const setGuestInviteToken = chatGuestSession.setInviteToken;
export const clearGuestInviteToken = chatGuestSession.clearInviteToken;
export const getGuestJwtClaims = chatGuestSession.getClaims;
export const isGuestJwtValid = chatGuestSession.isValid;
export const getGuestJwtExpiryMs = chatGuestSession.getExpiryMs;

export const redirectToGuestLogin = (assetURLCode) => {
  clearGuestJwt();
  if (assetURLCode) {
    window.location.assign(`/${assetURLCode}/chat2-guest`);
  }
};
